/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {Component} from '@angular/core';
import {CommonModule} from '@angular/common';
import {AbstractControl} from '@angular/forms';
import {IxModule} from '@siemens/ix-angular';
import {TranslateModule} from '@ngx-translate/core';
import {BaseFormFieldDirective} from '../../base-form-field.directive';
import {FieldLabelComponent} from '../field-label/field-label.component';
import {DocumentInputComponent} from '../document-input/document-input.component';
import {TextInputComponent} from '../text-input/text-input.component';
import {FormFieldAttribute} from '../../../../../../../models/FormCategory';
import {
  getDocumentUrlPath,
  toDocumentObject,
} from '../../../../../../shared/utils/document-object';

/**
 * Edits a single-object `document` field (a `document` leaf with sub-`attributes`,
 * e.g. `{url, language, resourceTitle}` in battery 2.0.0).
 *
 * Each sub-attribute is rendered as its own input and saved to
 * `<fullValuePath>.<subValuePath>`. Cleared sub-fields are saved as `null` (see
 * {@link BaseFormFieldDirective.checkValue}), and an object whose sub-fields are
 * all cleared is collapsed to `null` by the passport service before the PATCH, so
 * neither empty strings nor an empty object are sent.
 *
 * A legacy plain-string value (URL) is read as `{url: <string>}` and converted
 * to that object only when a sub-field is saved, so opening the form does not
 * count as a change.
 */
@Component({
  selector: 'app-document-object-input',
  imports: [
    CommonModule,
    IxModule,
    TranslateModule,
    FieldLabelComponent,
    DocumentInputComponent,
    TextInputComponent,
  ],
  templateUrl: './document-object-input.component.html',
  styleUrl: '../input-components.scss',
})
export class DocumentObjectInputComponent extends BaseFormFieldDirective {
  /**
   * Save function handed to the sub-field inputs. Converts a legacy string value
   * to its object form first, so the nested path can be written.
   */
  readonly subFieldSave = (
    value: string | number,
    attribute: FormFieldAttribute,
    control: AbstractControl,
    fullValuePath: string,
  ): void => {
    this.migrateLegacyValue();
    if (this.saveFunction) {
      this.saveFunction(value, attribute, control, fullValuePath);
    } else {
      this.changeControlValue(value, attribute, control, fullValuePath);
    }
  };

  private subAttributesCache?: {
    source: FormFieldAttribute;
    list: FormFieldAttribute[];
  };

  /**
   * The sub-attributes to render. A required document makes its url required,
   * so an empty required document shows the inline error. Cached per attribute
   * so the inputs keep a stable reference between change detections.
   */
  get subAttributes(): FormFieldAttribute[] {
    const source = this.attribute;
    if (this.subAttributesCache?.source !== source) {
      this.subAttributesCache = {
        source,
        list: (source.attributes ?? []).map((subAttribute) =>
          source.isRequired &&
          !subAttribute.isRequired &&
          this.isUrlAttribute(subAttribute)
            ? {...subAttribute, isRequired: true}
            : subAttribute,
        ),
      };
    }
    return this.subAttributesCache.list;
  }

  /** The control of this host component is not bound to any input. */
  override setComponentValue(): void {
    // Intentionally empty: the sub-field inputs own their controls.
  }

  /**
   * Returns the value of a sub-field, reading a legacy string value as `{url}`.
   *
   * @param subValuePath - The sub-attribute's value path, relative to the document.
   */
  getSubData(subValuePath: string): unknown {
    const document = toDocumentObject(
      this.passData,
      getDocumentUrlPath(this.attribute),
    );
    return (
      this.generalDataHandlingService.getValueByPath(document, subValuePath) ??
      ''
    );
  }

  isUrlAttribute(subAttribute: FormFieldAttribute): boolean {
    return subAttribute.type === 'url' || subAttribute.type === 'document';
  }

  /** Replaces a legacy string value in the passport data with its object form. */
  private migrateLegacyValue(): void {
    const passport = this.passportService.passes()[this.passportId];
    if (!passport?.data) {
      return;
    }
    const current = this.generalDataHandlingService.getValueByPath(
      passport.data,
      this.fullValuePath,
    );
    if (typeof current !== 'string') {
      return;
    }
    this.generalDataHandlingService.setValueByPath(
      passport.data,
      this.fullValuePath,
      toDocumentObject(current, getDocumentUrlPath(this.attribute)) ?? null,
    );
  }
}
